import { useCallback, useMemo } from "react"

import { format } from "date-fns"

import { isInternalStopType } from "@/lib/haulageUtils"
import {
  findRouteInstance,
  instanceStopForecast,
  planningBaseRouteId,
  type PlanningRouteCost,
} from "@/lib/planningRoutes"
import {
  applyPlannedMileageMultiplier,
  calculateDistanceFromPlannedStops,
  calculateDurationMinutes,
  calculateEmptyLegsDetailed,
  calculateVehicleCapacity,
  findVehicleType,
  priceRoute,
  type CapacityStop,
  type EmptyLegsDetail,
  type VehicleCapacityInfo,
} from "@/lib/routeCostCalculation"
import type {
  CollectionsConfig,
  Location,
  PlannedRoute,
  RouteInstance,
  VehicleType,
} from "@/types/collections"

export interface RouteMetrics {
  instance: RouteInstance | undefined
  volume: number
  /** Planning load factor: forecast volume per stop, unit type from the location, nothing actual. */
  capacityInfo: VehicleCapacityInfo | null
  /** The same legs the timeline draws red. */
  emptyLegs: EmptyLegsDetail
  cost: PlanningRouteCost | undefined
}

const stopTypeOf = (stop: { slotType?: string }) =>
  stop.slotType || (stop as { stop_type?: string }).stop_type

function planningListCapacityStop(
  stop: { slotType?: string; locationId: string },
  volume: number,
  location: Pick<Location, "loading_unit_type"> | undefined,
): CapacityStop {
  return {
    slotType: stopTypeOf(stop),
    locationId: stop.locationId,
    volumeEstimated: volume,
    loadingUnitsEstimated: 0,
    loadingUnitsType: location?.loading_unit_type,
  }
}

/** Keyed `<route id>|<yyyy-MM-dd>`; a missing row is computed on demand. */
export function useRouteMetrics({
  routes,
  routeInstances,
  vehicleTypes,
  config,
  locations,
  getLocationById,
}: {
  routes: PlannedRoute[]
  routeInstances: RouteInstance[] | undefined
  vehicleTypes: VehicleType[] | undefined
  config: CollectionsConfig | undefined
  locations: Location[] | undefined
  getLocationById: (locationId: string) => Location | undefined
}) {
  // Fallback: instance priced on its own day, for rows the planned pricing couldn't cost.
  const routeInstanceCosts = useMemo(() => {
    const costs = new Map<string, ReturnType<typeof priceRoute>>()
    if (!vehicleTypes || !config || !routeInstances) return costs

    routeInstances.forEach((instance: RouteInstance) => {
      const cost = priceRoute({
        vehicleTypes,
        vehicleTypeRef: instance.planned_route?.vehicle_type,
        config,
        distanceMiles: instance.distance_miles,
        durationMinutes: instance.duration_minutes,
        day: {
          stops: instance.stops,
          crewSize: instance.crew_size,
          routeKind: instance.route_kind,
        },
        plan: {
          crewSize: instance.planned_route?.crew_size,
          routeKind: instance.planned_route?.route_kind,
        },
      })
      if (cost) costs.set(`${instance.planned_route_id || instance.id}|${instance.date}`, cost)
    })

    return costs
  }, [routeInstances, vehicleTypes, config])

  const computeMetrics = useCallback(
    (route: PlannedRoute): RouteMetrics => {
      const dateStr = format(route.date, "yyyy-MM-dd")
      const key = `${route.id}|${dateStr}`
      const instance = findRouteInstance(routeInstances, planningBaseRouteId(route.id), dateStr)
      const forecastFor = (stop: { locationId?: string }) => instanceStopForecast(instance, stop)
      const stops = route.stops || []

      const volume = stops.reduce((acc, s) => acc + forecastFor(s), 0)

      let capacityInfo: VehicleCapacityInfo | null = null
      const vehicleType = findVehicleType(vehicleTypes, route.vehicleType)
      const hasStopsForCapacity = stops.some((stop) => !isInternalStopType(stopTypeOf(stop)))
      if (vehicleType && hasStopsForCapacity) {
        // Internal stops are skipped inside calculateVehicleCapacity.
        capacityInfo = calculateVehicleCapacity(
          vehicleType,
          stops.map((stop) =>
            planningListCapacityStop(stop, forecastFor(stop), getLocationById(stop.locationId)),
          ),
          locations,
        )
      }

      const emptyLegs = calculateEmptyLegsDetailed(stops, (i) => forecastFor(stops[i]) || 0)

      let plannedCost: ReturnType<typeof priceRoute> = null
      if (vehicleTypes && config) {
        const rawDistanceMiles =
          route.stops && route.stops.length >= 2
            ? calculateDistanceFromPlannedStops(route.stops)
            : null
        const distanceMiles =
          applyPlannedMileageMultiplier(rawDistanceMiles, config) ?? rawDistanceMiles

        const durationMinutes =
          route.plannedStartTime && route.plannedEndTime
            ? calculateDurationMinutes(route.plannedStartTime, route.plannedEndTime)
            : null

        // The generated day, if any, prices ahead of the plan.
        plannedCost = priceRoute({
          vehicleTypes,
          vehicleTypeRef: route.vehicleType,
          config,
          distanceMiles,
          durationMinutes,
          day: instance && {
            stops: instance.stops,
            crewSize: instance.crew_size,
            routeKind: instance.route_kind,
          },
          plan: { stops: route.stops, crewSize: route.crewSize, routeKind: route.route_kind },
        })
      }
      const cost = (plannedCost || routeInstanceCosts.get(key)) ?? undefined

      return { instance, volume, capacityInfo, emptyLegs, cost }
    },
    [routeInstances, vehicleTypes, config, locations, getLocationById, routeInstanceCosts],
  )

  const metricsByKey = useMemo(() => {
    const map = new Map<string, RouteMetrics>()
    routes.forEach((route) => {
      map.set(`${route.id}|${format(route.date, "yyyy-MM-dd")}`, computeMetrics(route))
    })
    return map
  }, [routes, computeMetrics])

  return useCallback(
    (route: PlannedRoute): RouteMetrics =>
      metricsByKey.get(`${route.id}|${format(route.date, "yyyy-MM-dd")}`) ??
      computeMetrics(route),
    [metricsByKey, computeMetrics],
  )
}
